import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  limit,
  query,
  setDoc,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { getDb } from '../firebase';

/**
 * Check whether a user has an admin doc. Non-admins see no docs under /admins
 * except their own (per firestore.rules), so this is a one-doc existence read.
 */
export async function checkIsAdmin(uid) {
  if (!uid) return false;
  try {
    const snap = await getDoc(doc(getDb(), `admins/${uid}`));
    return snap.exists();
  } catch {
    return false;
  }
}

/**
 * Subscribe to the last N days of platformStats daily snapshots, ordered oldest-first.
 * Returns an unsubscribe function.
 */
export function subscribeToPlatformStats(days, callback) {
  const db = getDb();
  // platformStats doc IDs are YYYY-MM-DD strings which sort lexicographically
  // equivalent to chronological order — no index needed.
  const q = query(
    collection(db, 'platformStats'),
    where('date', '>=', daysAgoKey(days)),
    orderBy('date', 'asc'),
    limit(days + 1)
  );
  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => d.id !== '_totals');
      callback(docs);
    },
    (err) => {
      console.error('[adminMetrics] platformStats subscribe failed', err);
      callback([]);
    }
  );
}

/**
 * Subscribe to the running totals document (platformStats/_totals), which holds
 * running counters maintained by Firestore triggers.
 */
export function subscribeToTotals(callback) {
  const db = getDb();
  return onSnapshot(
    doc(db, 'platformStats/_totals'),
    (snap) => callback(snap.exists() ? snap.data() : null),
    (err) => {
      console.error('[adminMetrics] totals subscribe failed', err);
      callback(null);
    }
  );
}

/**
 * Subscribe to all cohort retention docs (cohorts/{YYYY-WW}). Each doc summarizes
 * signup count and weekN retention for users who signed up in that ISO week.
 */
export function subscribeToCohorts(weeks, callback) {
  const db = getDb();
  const q = query(
    collection(db, 'cohorts'),
    orderBy('__name__', 'asc'),
    limit(weeks)
  );
  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(docs);
    },
    (err) => {
      console.error('[adminMetrics] cohorts subscribe failed', err);
      callback([]);
    }
  );
}

/**
 * Subscribe to today's per-user active-users subcollection. Each session-ping
 * write lands here in real time, so the dashboard can show today's DAU live —
 * the daily rollup wouldn't otherwise include today until tomorrow's run.
 */
export function subscribeToTodayActiveUsers(callback) {
  const db = getDb();
  const today = new Date().toISOString().slice(0, 10);
  return onSnapshot(
    collection(db, `platformStats/${today}/activeUsers`),
    (snap) => {
      const counts = { web: 0, ios: 0, android: 0 };
      snap.forEach((d) => {
        const platform = d.data().platform;
        if (counts[platform] !== undefined) counts[platform] += 1;
      });
      callback({ ...counts, total: counts.web + counts.ios + counts.android });
    },
    (err) => {
      console.error('[adminMetrics] todayActiveUsers subscribe failed', err);
      callback({ web: 0, ios: 0, android: 0, total: 0 });
    }
  );
}

/**
 * Subscribe to the last N days of pageviews docs.
 */
export function subscribeToPageviews(days, callback) {
  const db = getDb();
  const q = query(
    collection(db, 'pageviews'),
    where('__name__', '>=', daysAgoKey(days)),
    orderBy('__name__', 'asc'),
    limit(days + 1)
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('[adminMetrics] pageviews subscribe failed', err);
      callback([]);
    }
  );
}

/**
 * YYYY-MM-DD key for N days before today (UTC). Exported so callers that
 * subscribe to a wide window and then narrow it client-side use the same
 * boundary the Firestore queries do.
 */
export function daysAgoKey(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Subscribe to the last N days of socialMetrics docs (manual daily entry by
 * admin: Threads + X follower counts, impressions, profile visits, etc.).
 * Doc IDs are YYYY-MM-DD so name-order is chronological.
 */
export function subscribeToSocialMetrics(days, callback) {
  const db = getDb();
  const q = query(
    collection(db, 'socialMetrics'),
    where('__name__', '>=', daysAgoKey(days)),
    orderBy('__name__', 'asc'),
    limit(days + 1)
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('[adminMetrics] socialMetrics subscribe failed', err);
      callback([]);
    }
  );
}

/**
 * Subscribe to the last N days of outbound-email send counts
 * (emailStats/{YYYY-MM-DD}, each holding `byType` + `total`). Doc IDs are
 * YYYY-MM-DD so name-order is chronological.
 *
 * The `_totals` sentinel is filtered out: `_` (0x5F) sorts AFTER the digits in
 * a date key, so a `>= daysAgo` range query DOES return `_totals`. The email
 * panel only ever looks docs up by date key, so it never noticed — but any
 * consumer that sums raw docs would double-count lifetime into the window.
 * Match subscribeToPlatformStats and drop it here so no consumer can trip.
 */
export function subscribeToEmailStats(days, callback) {
  const db = getDb();
  const q = query(
    collection(db, 'emailStats'),
    where('__name__', '>=', daysAgoKey(days)),
    orderBy('__name__', 'asc'),
    limit(days + 1)
  );
  return onSnapshot(
    q,
    (snap) =>
      callback(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => d.id !== '_totals')
      ),
    (err) => {
      console.error('[adminMetrics] emailStats subscribe failed', err);
      callback([]);
    }
  );
}

/**
 * Subscribe to lifetime email send counters (emailStats/_totals).
 */
export function subscribeToEmailTotals(callback) {
  const db = getDb();
  return onSnapshot(
    doc(db, 'emailStats/_totals'),
    (snap) => callback(snap.exists() ? snap.data() : null),
    (err) => {
      console.error('[adminMetrics] emailStats totals subscribe failed', err);
      callback(null);
    }
  );
}

/**
 * Subscribe to the last N days of push-notification send counts
 * (pushStats/{YYYY-MM-DD}, each holding `byType`, `byPlatform`, and `total`).
 * Doc IDs are YYYY-MM-DD so name-order is chronological.
 *
 * `_totals` is filtered out: its `_`-prefixed id sorts AFTER date keys, so the
 * `>= daysAgo` range query returns it. PushSentPanel sums `byPlatform` across
 * these docs for its window tiles, so a stray `_totals` would add the lifetime
 * platform counts on top of the daily ones and roughly double the window.
 */
export function subscribeToPushStats(days, callback) {
  const db = getDb();
  const q = query(
    collection(db, 'pushStats'),
    where('__name__', '>=', daysAgoKey(days)),
    orderBy('__name__', 'asc'),
    limit(days + 1)
  );
  return onSnapshot(
    q,
    (snap) =>
      callback(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => d.id !== '_totals')
      ),
    (err) => {
      console.error('[adminMetrics] pushStats subscribe failed', err);
      callback([]);
    }
  );
}

/**
 * Subscribe to lifetime push send counters (pushStats/_totals).
 */
export function subscribeToPushTotals(callback) {
  const db = getDb();
  return onSnapshot(
    doc(db, 'pushStats/_totals'),
    (snap) => callback(snap.exists() ? snap.data() : null),
    (err) => {
      console.error('[adminMetrics] pushStats totals subscribe failed', err);
      callback(null);
    }
  );
}

/**
 * Subscribe to the last N days of account-deletion counts
 * (accountDeletions/{YYYY-MM-DD}, each holding a `count`). Doc IDs are
 * YYYY-MM-DD so name-order is chronological.
 *
 * `_totals` is filtered out: its `_`-prefixed id sorts AFTER date keys, so the
 * `>= daysAgo` range query returns it. The deleted-accounts card sums `count`
 * over trailing windows, so a stray `_totals` would inflate every window by the
 * lifetime deletion count. Match the emailStats/pushStats subscribers and drop
 * it here so no consumer can trip.
 */
export function subscribeToAccountDeletions(days, callback) {
  const db = getDb();
  const q = query(
    collection(db, 'accountDeletions'),
    where('__name__', '>=', daysAgoKey(days)),
    orderBy('__name__', 'asc'),
    limit(days + 1)
  );
  return onSnapshot(
    q,
    (snap) =>
      callback(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => d.id !== '_totals')
      ),
    (err) => {
      console.error('[adminMetrics] accountDeletions subscribe failed', err);
      callback([]);
    }
  );
}

/**
 * Read a single socialMetrics doc by date (YYYY-MM-DD), used to pre-populate
 * the admin entry form when an admin opens the page mid-day to update numbers.
 */
export async function getSocialMetricsForDate(date) {
  try {
    const snap = await getDoc(doc(getDb(), `socialMetrics/${date}`));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.error('[adminMetrics] getSocialMetricsForDate failed', err);
    return null;
  }
}

/**
 * Write a socialMetrics doc for the given date. `payload` shape:
 *   { threads: { followers, impressions, profileVisits, postsPublished, repliesGiven },
 *     x:       { followers, impressions, profileVisits, postsPublished, repliesGiven } }
 * Numeric fields default to 0 when unset.
 */
export async function saveSocialMetrics(date, uid, payload) {
  const db = getDb();
  await setDoc(
    doc(db, `socialMetrics/${date}`),
    {
      threads: payload.threads || {},
      x: payload.x || {},
      recordedBy: uid || null,
      recordedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
